import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiAdmin, setSuperAdminToken } from '@/api/apiClient';
import { Building2, CreditCard, MapPinned, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

const formatMoney = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const chartConfig = {
  total: {
    label: 'Empresas',
    color: 'hsl(var(--chart-1))',
  },
};

export default function AdminDashboard() {
  const [metricas, setMetricas] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [crescimento, setCrescimento] = useState([]);
  const [integracaoAsaas, setIntegracaoAsaas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const [met, mrrRes, cresc, asaas] = await Promise.all([
        apiAdmin.get('/api/admin/metricas'),
        apiAdmin.get('/api/admin/mrr'),
        apiAdmin.get('/api/admin/crescimento'),
        apiAdmin.get('/api/admin/integracao-asaas').catch(() => null),
      ]);
      setMetricas(met);
      setMrr(mrrRes);
      setCrescimento(Array.isArray(cresc) ? cresc : []);
      setIntegracaoAsaas(asaas);
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(err?.message || 'Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const statusCounts = (metricas?.empresas_por_status || []).reduce((acc, item) => {
    acc[item.status] = item._count;
    return acc;
  }, {});

  const empresasAtivas = (statusCounts.active || 0) + (statusCounts.trial || 0);

  const statsCards = [
    {
      title: 'Empresas ativas',
      value: empresasAtivas,
      icon: Building2,
      className: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Total usuários',
      value: metricas?.total_usuarios ?? 0,
      icon: Users,
      className: 'from-emerald-500 to-emerald-600',
    },
    {
      title: 'Receita mensal',
      value: formatMoney(metricas?.receita_mensal),
      icon: TrendingUp,
      className: 'from-violet-500 to-violet-600',
    },
    {
      title: 'Novos cadastros (30d)',
      value: metricas?.novos_cadastros_30d ?? 0,
      icon: Building2,
      className: 'from-amber-500 to-amber-600',
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600">Visão geral da plataforma</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/admin/empresas">Ver empresas</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin/planos">Planos</Link>
          </Button>
          <Button asChild variant="secondary" className="gap-1.5">
            <Link to="/admin/config/guided-tour" className="flex items-center gap-1.5">
              <MapPinned className="h-4 w-4 shrink-0" />
              Tour guiado
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="overflow-hidden border-0 shadow-lg">
              <div className={`bg-gradient-to-br ${stat.className} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-white/90 uppercase tracking-wide">
                    {stat.title}
                  </p>
                  <Icon className="h-5 w-5 text-white/80" />
                </div>
                <p className="mt-2 text-2xl font-bold">{stat.value}</p>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>MRR / ARR</CardTitle>
            <CardDescription>
              {mrr?.total_assinaturas ?? 0} assinatura(s) ativa(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-600">MRR</span>
              <span className="font-semibold">{formatMoney(mrr?.mrr_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">ARR</span>
              <span className="font-semibold">{formatMoney(mrr?.arr_total)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Empresas por status</CardTitle>
            {metricas?.taxa_conversao != null && (
              <CardDescription>Taxa conversão: {metricas.taxa_conversao}%</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {[
                { key: 'active', label: 'Ativo' },
                { key: 'trial', label: 'Trial' },
                { key: 'suspended', label: 'Suspenso' },
                { key: 'cancelled', label: 'Cancelado' },
              ].map(({ key, label }) => (
                <li key={key} className="flex justify-between items-center">
                  <Badge variant={key === 'active' ? 'default' : key === 'trial' ? 'secondary' : 'outline'}>
                    {label}
                  </Badge>
                  <span className="font-medium">{statusCounts[key] ?? 0}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {crescimento.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Crescimento (últimos 6 meses)</CardTitle>
            <CardDescription>Novas empresas por mês</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[200px] w-full">
              <BarChart data={crescimento}>
                <XAxis dataKey="mes" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {integracaoAsaas && (
        <Card>
          <CardHeader>
            <CardTitle>Integração ASAAS</CardTitle>
            <CardDescription>
              Status: {integracaoAsaas.hasApiKey ? (
                <Badge variant="default" className="ml-1">API configurada</Badge>
              ) : (
                <Badge variant="destructive" className="ml-1">Configure ASAAS_API_KEY no servidor</Badge>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-slate-600">URL do webhook (configure no painel ASAAS):</p>
            <code className="block text-xs bg-slate-100 p-3 rounded break-all font-mono">
              {integracaoAsaas.webhookUrl}
            </code>
            <Button asChild variant="link" className="p-0 h-auto">
              <a href={integracaoAsaas.asaasBaseUrl} target="_blank" rel="noopener noreferrer">
                Abrir painel ASAAS →
              </a>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
